import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Eye, EyeOff, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getCalendarBookings } from '../../lib/bookings';
import { getBookingFeedback } from '../../lib/feedback';
import { authStateManager } from '../../lib/authStateManager';
import {
  pastDueBillingLockRequired,
  stripeSyncedStatusAllowsNexusMainApp,
} from '../../lib/subscription';
import BasicLoadingScreen from '../../components/BasicLoadingScreen';
import { DEFAULT_HOME_ROUTE, getHomeScreenRouteFromAppRow } from '../../lib/controlCentreHome';
import { signOut } from '../../lib/auth';
import { callerTeamAccess, DEACTIVATED_TEAM_REASON } from '../../lib/teamAccess';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingStage, setLoadingStage] = useState<string>('');
  const [showLoginAnimation, setShowLoginAnimation] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    // Simplified approach - let Supabase handle token refresh automatically
    const checkAuth = async () => {
      // Only check auth if we're actually on the login page
      // Prevents infinite loops if user is already logged in
      if (window.location.pathname !== '/login') {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      
      // Set a maximum timeout to ensure we always stop loading
      const maxTimeout = setTimeout(() => {
        console.warn('Login: Auth check taking too long, stopping...');
        setIsLoading(false);
      }, 10000); // 10 second absolute maximum
      
      try {
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }
        
        // Get current session - Supabase will automatically refresh if needed
        // This is simpler and more reliable than manual refresh
        const { data: { session } } = await supabase.auth.getSession();
        
        clearTimeout(maxTimeout);
        
        // If we have a session, user is logged in - navigate away
        if (session?.user) {
          console.log('✅ Login: User already logged in, redirecting...');
          setIsLoading(false);
          navigate('/', { replace: true });
          return;
        }
        
        // No session - user needs to log in
        // Supabase's autoRefreshToken will handle refresh attempts automatically
        setIsLoading(false);
      } catch (err) {
        console.error('Auth check error:', err);
        clearTimeout(maxTimeout);
        setError(err instanceof Error ? err.message : 'Failed to check authentication status');
        setIsLoading(false);
      }
    };

    checkAuth();

    // Listen for auth state changes using centralized manager
    if (!supabase) {
      console.error('Supabase client not initialized');
      setIsLoading(false);
      return;
    }
    
    const unsubscribe = authStateManager.subscribe((_event, session) => {
      // Only handle auth state changes if we're on the login page
      // Prevents navigation loops
      if (window.location.pathname !== '/login') {
        return;
      }
      
      if (session?.user) {
        // Check if enhanced login is handling navigation
        const enhancedLoginInProgress = localStorage.getItem('enhancedLoginInProgress');
        if (enhancedLoginInProgress === 'true') {
          console.log('🚀 Auth state change detected, but enhanced login is handling navigation');
          // Clean up the flag and let enhanced login handle the navigation
          localStorage.removeItem('enhancedLoginInProgress');
          return;
        }
        
        // Only navigate if we're still on the login page (double check)
        if (window.location.pathname === '/login') {
          console.log('🚀 Auth state change - navigating to home');
          setIsLoading(false); // Ensure loading is cleared before navigation
          navigate('/', { replace: true });
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [navigate]);

  const validateForm = () => {
    const errors = {
      email: '',
      password: ''
    };

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Only validate that password is not empty
    if (!password.trim()) {
      errors.password = 'Password is required'; 
    }

    setValidationErrors(errors);
    return !errors.email && !errors.password;
  };

  const preloadCalendarData = async (selectedBusiness: any) => {
    if (!supabase) throw new Error('Supabase client not initialized');

    try {
      setLoadingStage('Building workflows.....');
      console.log('🗓️ Preloading calendar data for seamless experience...');

      // Calculate current month date range
      const today = new Date();
      const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      currentMonthStart.setHours(0, 0, 0, 0);
      
      const currentMonthEnd = new Date(currentMonthStart);
      currentMonthEnd.setMonth(currentMonthEnd.getMonth() + 1);
      currentMonthEnd.setDate(0);
      currentMonthEnd.setHours(23, 59, 59, 999);

      console.log('🗓️ Date range:', { 
        start: currentMonthStart.toISOString(), 
        end: currentMonthEnd.toISOString() 
      });

      // Load calendar bookings (location bookings for CalendarV2)
      const calendarBookings = await getCalendarBookings(
        selectedBusiness.id,
        currentMonthStart,
        currentMonthEnd,
        true, // isLocationBooking = true for CalendarV2
        undefined // No resource filtering during preload
      );

      console.log(`🗓️ Loaded ${calendarBookings.length} calendar bookings`);

      // Load time off requests for the same date range
      const { data: timeOffData } = await supabase
        .from('time_off_requests')
        .select(`
          id,
          start_date,
          end_date,
          request_type,
          is_partial_day,
          start_time,
          end_time,
          resource_id,
          resources:resource_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'approved')
        .lte('start_date', currentMonthEnd.toISOString().split('T')[0])
        .gte('end_date', currentMonthStart.toISOString().split('T')[0]);

      console.log(`🗓️ Loaded ${timeOffData?.length || 0} time off requests`);

      // Calculate total sales for current month
      setLoadingStage('Creating Environment.....');
      const formattedMonthStart = currentMonthStart.toISOString().split('T')[0];
      const formattedMonthEnd = currentMonthEnd.toISOString().split('T')[0];

      const { data: salesData, error: salesError } = await supabase
        .from('bookings')
        .select('id, booking_number, total_amount, tax_amount, subtotal, end_date, mobile_booking')
        .eq('business_id', selectedBusiness.id)
        .eq('mobile_booking', false) // Only location bookings
        .gte('end_date', formattedMonthStart)
        .lte('end_date', formattedMonthEnd);

      let totalSales = 0;
      if (!salesError && salesData) {
        totalSales = salesData.reduce((acc, booking) => {
          if (booking.subtotal !== null && booking.subtotal !== undefined) {
            return acc + booking.subtotal;
          }
          const taxAmount = booking.tax_amount || 0;
          const totalAmount = booking.total_amount || 0;
          return acc + (totalAmount - taxAmount);
        }, 0);
      }

      console.log(`💰 Calculated total sales: $${totalSales}`);

      // Load feedback for completed bookings
      setLoadingStage('Analyzing Data.........');
      const completedBookings = calendarBookings.filter(
        booking => booking.extendedProps.status === 'completed'
      );

      const feedbackMap = new Map();
      if (completedBookings.length > 0) {
        await Promise.all(
          completedBookings.map(async (booking) => {
            try {
              const feedback = await getBookingFeedback(booking.id);
              if (feedback) {
                // Calculate average rating
                let averageRating = 0;
                let ratingCount = 0;
                
                if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
                  Object.values(feedback.form_responses).forEach(value => {
                    if (typeof value === 'number' && value > 0) {
                      averageRating += value;
                      ratingCount++;
                    }
                  });
                } else {
                  if (feedback.overall_quality_rating) {
                    averageRating += feedback.overall_quality_rating;
                    ratingCount++;
                  }
                  if (feedback.overall_experience_rating) {
                    averageRating += feedback.overall_experience_rating;
                    ratingCount++;
                  }
                }
                
                if (ratingCount > 0) {
                  averageRating = averageRating / ratingCount;
                  feedbackMap.set(booking.id, {
                    rating: averageRating,
                    hasText: !!(feedback.improvement_suggestions || 
                              (feedback.form_responses && Object.values(feedback.form_responses).some(v => typeof v === 'string' && v.trim())))
                  });
                }
              }
            } catch (error) {
              console.error('Error loading feedback for booking:', booking.id, error);
            }
          })
        );
      }

      console.log(`💬 Loaded feedback for ${feedbackMap.size} completed bookings`);

      // Store all calendar data for instant access
      const calendarData = {
        bookings: calendarBookings,
        totalSales,
        bookingFeedback: Array.from(feedbackMap.entries()),
        currentMonthStart: currentMonthStart.toISOString(),
        currentMonthEnd: currentMonthEnd.toISOString(),
        initialDate: today.toISOString(), // For scroll positioning
        timestamp: Date.now()
      };

      localStorage.setItem('preloadedCalendarData', JSON.stringify(calendarData));
      console.log('🗓️ Calendar data preloaded and stored successfully');

    } catch (error) {
      console.error('❌ Error preloading calendar data:', error);
      // Don't fail the login if calendar preloading fails
      // The calendar will fall back to normal loading
    }
  };

  const determineHomeScreen = async (businessId: string) => {
    if (!supabase) throw new Error('Supabase client not initialized');

    try {
      setLoadingStage('Creating Environment.....');

      // NEW SYSTEM: Check business_apps_v2 for home screen app
      // Note: We still determine it here for preloading, but we'll navigate to / 
      // so HomeScreenRedirect can query fresh (ensures resources see latest setting)
      const { data: homeScreenApp, error: homeError } = await supabase
        .from('business_apps_v2')
        .select(`
          app_id,
          app:app_registry_v2(
            id,
            slug,
            route_prefix
          )
        `)
        .eq('business_id', businessId)
        .eq('is_home_screen', true)
        .maybeSingle();

      const targetRoute = getHomeScreenRouteFromAppRow(homeScreenApp);
      if (!homeError && targetRoute) {
        console.log('🏠 Home screen determined from installed apps:', targetRoute);
        return targetRoute;
      }

      console.log('No home screen set, using Control Centre');
      return DEFAULT_HOME_ROUTE;
      
    } catch (error) {
      console.log('Error determining home screen, using default:', error);
      return DEFAULT_HOME_ROUTE;
    }
  };

  const loadBusinessesAndDetermineRoute = async (userId: string): Promise<string | null> => {
    if (!supabase) throw new Error('Supabase client not initialized');

    try {
      setLoadingStage('Building workflows.....');
      
      // CRITICAL: Clear ALL stale business data before loading new businesses
      // This ensures we don't use business data from previous login
      // MUST clear BEFORE any database queries to prevent race conditions
      console.log('🧹 Clearing ALL stale business data before loading new businesses...');
      const staleKeys = [
        'selectedBusiness',
        'businesses',
        'usePreloadedBusinessData',
        'homeScreenRoute',
        'skipInitializing',
        'preloadedRoute',
        'preloadedCalendarData', // Also clear calendar preload data
        'lastSelectedBusinessId',
        'businessContext',
        'userBusinesses',
        'currentBusiness',
        'enhancedLoginInProgress'
      ];
      staleKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Cleared stale key: ${key}`);
        } catch (e) {
          console.warn(`Failed to clear ${key}:`, e);
        }
      });
      
      // CRITICAL: Wait a moment to ensure BusinessContext has processed the clear
      // This prevents race conditions where BusinessContext reads stale data
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // First, check if user is a business owner (admin)
      const { data: ownedBusinesses, error: ownedBusinessError } = await supabase
        .from('businesses')
        .select('*')
        .eq('owner_id', userId)
        .order('created_at', { ascending: true });

      if (ownedBusinessError) {
        console.error('Error loading owned businesses:', ownedBusinessError);
        throw new Error('Failed to load business data');
      }

      // If user owns businesses, use the first one
      if (ownedBusinesses && ownedBusinesses.length > 0) {
        const selectedBusiness = ownedBusinesses[0];
        console.log('User is business owner, using business:', selectedBusiness.id);
        
        // Check subscription status for business owner (including trial)
        console.log('Checking subscription status for business:', selectedBusiness.id);
        console.log('Subscription status:', selectedBusiness.subscription_status);
        console.log('Trial enabled:', selectedBusiness.trial_enabled);
        console.log('Trial end date:', selectedBusiness.trial_end_date);
        
        const trialActive =
          !!selectedBusiness.trial_enabled &&
          !!selectedBusiness.trial_end_date &&
          new Date(selectedBusiness.trial_end_date) > new Date();
        const isBusinessActive =
          stripeSyncedStatusAllowsNexusMainApp(
            selectedBusiness.subscription_status,
            selectedBusiness.subscription_past_due_at,
          ) ||
          trialActive;

        if (!isBusinessActive) {
          console.log('❌ Subscription is inactive and no active trial, redirecting to subscription management');
          // DON'T store business data in localStorage - BusinessContext will load it fresh
          navigate('/admin-subscription-management', { replace: true });
          return null;
        }
        
        // DON'T store business data in localStorage - BusinessContext will load it fresh from database
        
        // Continue with normal flow for business owners
        await preloadCalendarData(selectedBusiness);
        return await determineHomeScreen(selectedBusiness.id);
      }

      // If user doesn't own any businesses, check if they're a resource
      console.log('User is not a business owner, checking if they are a resource...');
      
      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select(`
          *,
          businesses!inner (*)
        `)
        .eq('auth_user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (resourceError) {
        console.error('Error loading resource data:', resourceError);
        throw new Error('Failed to load resource data');
      }

      if (!resourceData || resourceData.length === 0) {
        console.log('User is not a resource either, redirecting to setup');
        return '/setup'; // User needs to be added to a business
      }

      // User is a resource, use their first active business
      const resource = resourceData[0];
      const selectedBusiness = resource.businesses;
      
      console.log('User is a resource for business:', selectedBusiness.id);
      console.log('Resource subscription status:', resource.subscription_status);
      console.log('Business subscription status:', selectedBusiness.subscription_status);
      
      // Check business subscription status first (including trial)
      const trialActive =
        !!selectedBusiness.trial_enabled &&
        !!selectedBusiness.trial_end_date &&
        new Date(selectedBusiness.trial_end_date) > new Date();
      const isBusinessActive =
        stripeSyncedStatusAllowsNexusMainApp(
          selectedBusiness.subscription_status,
          selectedBusiness.subscription_past_due_at,
        ) ||
        trialActive;

      if (!isBusinessActive) {
        console.log('❌ Business subscription is inactive and no active trial, redirecting resource to expired screen');
        // DON'T store business data in localStorage - BusinessContext will load it fresh
        navigate('/resource-subscription-expired', { replace: true });
        return null;
      }
      
      // Check resource subscription status
      if (resource.subscription_status === 'inactive') {
        console.log('❌ Resource subscription is inactive, access denied');
        throw new Error('Your account access has been suspended. Please contact your administrator.');
      }
      
      // DON'T store business data in localStorage - BusinessContext will load it fresh from database
      
      // Continue with normal flow for resources
      await preloadCalendarData(selectedBusiness);
      return await determineHomeScreen(selectedBusiness.id);
    } catch (error) {
      console.error('Error during business/route loading:', error);
      // Return default route on error
      return '/calendarV2';
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setError(null);
    setValidationErrors({
      email: '',
      password: ''
    });
    setLoading(true);
    setLoadingStage('Validating credentials...');

    if (!validateForm()) {
      setLoading(false);
      setLoadingStage('');
      return;
    }

    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Enhanced debugging for Android devices
      const isAndroid = /Android/.test(navigator.userAgent);
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    (window.navigator as any).standalone === true ||
                    document.referrer.includes('android-app://');
      
      console.log('🔍 Login attempt details:', {
        isAndroid,
        isPWA,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        hasLocalStorage: typeof Storage !== 'undefined',
        hasIndexedDB: 'indexedDB' in window
      });

      // DEBUG: Log sanitized credential info
      console.log('🔐 Credential check:', {
        email: email.trim(),
        emailLength: email.trim().length,
        hasPassword: !!password,
        passwordLength: password.length,
        passwordNotEmpty: password.trim().length > 0
      });

      // CRITICAL: Clear ALL leftover state from previous login attempts or business switches
      // This ensures a completely fresh login with no stale data
      const staleKeys = [
        'enhancedLoginInProgress',
        'selectedBusiness',
        'businesses',
        'usePreloadedBusinessData',
        'skipInitializing',
        'preloadedRoute',
        'homeScreenRoute',
        'lastSelectedBusinessId',
        'businessContext',
        'userBusinesses',
        'currentBusiness'
      ];
      staleKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Cleared stale key before login: ${key}`);
        } catch (e) {
          console.warn(`Failed to clear stale key ${key}:`, e);
        }
      });
      
      // Set flag to prevent auth state change listener from interfering
      localStorage.setItem('enhancedLoginInProgress', 'true');
      
      // Add timeout for Android devices that might have slower connections
      const timeoutDuration = isAndroid ? 30000 : 15000; // Increased timeout to prevent premature failures
      
      console.log('🚀 Calling supabase.auth.signInWithPassword...');
      
      // Simplified login - single attempt with proper timeout
      // Removed retry logic to prevent multiple failures
      console.log('🚀 Attempting login...');
      
      const loginPromise = supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim()
      });
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Login timeout after ${timeoutDuration / 1000} seconds`));
        }, timeoutDuration);
      });
      
      // Race between login and timeout
          const result = await Promise.race([loginPromise, timeoutPromise]) as any;
      
      let data: any = null;
      let error: any = null;
          
          if (result.error) {
            error = result.error;
          } else if (result.data) {
            data = result.data;
      } else {
        error = new Error('No data or error returned from login');
      }

      // DEBUG: Log raw response
      console.log('📡 Auth response:', {
        hasData: !!data,
        hasError: !!error,
        hasSession: !!data?.session,
        hasUser: !!data?.user,
        userId: data?.user?.id,
        userEmail: data?.user?.email,
        errorMessage: error?.message,
        errorStatus: error?.status,
        errorCode: error?.code
      });

      if (error) {
        // Clear the flag on error
        localStorage.removeItem('enhancedLoginInProgress');
        
        // Enhanced error logging for Android debugging
        console.error('❌ Login error details:', {
          error: error.message,
          code: error.status,
          errorCode: error.code,
          hint: error.hint,
          details: error.details,
          isAndroid,
          userAgent: navigator.userAgent,
          fullError: error
        });
        
        if (/ban/i.test(error.message || '')) {
          setShowLoginAnimation(false);
          navigate('/account-disabled', {
            replace: true,
            state: { reason: DEACTIVATED_TEAM_REASON, email: email.trim() },
          });
          return;
        }
        throw error;
      }

      if (!data.session || !data.user) {
        // Clear the flag on error
        localStorage.removeItem('enhancedLoginInProgress');
        console.error('❌ No session/user despite no error:', {
          hasSession: !!data.session,
          hasUser: !!data.user,
          dataKeys: Object.keys(data || {}),
          rawData: data
        });
        throw new Error('No session returned from authentication');
      }

      const teamAccess = await callerTeamAccess();
      if (teamAccess === 'deactivated') {
        localStorage.removeItem('enhancedLoginInProgress');
        setShowLoginAnimation(false);
        await signOut();
        navigate('/account-disabled', {
          replace: true,
          state: { reason: DEACTIVATED_TEAM_REASON, email: email.trim() },
        });
        return;
      }

      console.log('✅ Authentication successful!');
      console.log('🎉 Success details:', {
        userId: data.user.id,
        userEmail: data.user.email,
        userRole: data.user.role,
        userMetadata: data.user.user_metadata,
        appMetadata: data.user.app_metadata,
        emailConfirmed: data.user.email_confirmed_at,
        lastSignIn: data.user.last_sign_in_at,
        sessionAccessToken: data.session?.access_token ? 'Present' : 'Missing',
        sessionRefreshToken: data.session?.refresh_token ? 'Present' : 'Missing',
        sessionExpiresAt: data.session?.expires_at,
        sessionExpiresIn: data.session?.expires_in
      });
      
      // Simplified session verification - reduced attempts to prevent delays
      // Supabase session should be immediately available after signInWithPassword
      console.log('⏳ Verifying session...');
      let sessionVerified = false;
      const maxVerificationAttempts = 3; // Reduced from 10 to 3
      
      for (let attempt = 0; attempt < maxVerificationAttempts; attempt++) {
        try {
        const { data: { session: verifiedSession }, error: verifyError } = await supabase.auth.getSession();
          
          if (verifyError) {
            console.warn(`⚠️ Session verification error on attempt ${attempt + 1}:`, verifyError);
          }
          
          if (verifiedSession && verifiedSession.user?.id === data.user.id) {
          sessionVerified = true;
          console.log(`✅ Session verified on attempt ${attempt + 1}`);
          break;
        }
          
          if (attempt < maxVerificationAttempts - 1) {
            const waitTime = 100; // Fixed 100ms delay instead of exponential
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        } catch (verifyErr) {
          console.warn(`⚠️ Session verification exception on attempt ${attempt + 1}:`, verifyErr);
          if (attempt < maxVerificationAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }
      
      if (!sessionVerified) {
        console.warn('⚠️ Session verification incomplete, but continuing with login...');
        // Don't throw error - session should still be valid
      }
      
      console.log('✅ Checking subscription and setup status...');

      // Log login activity (non-blocking)
      setTimeout(async () => {
        try {
          const { logLogin } = await import('../../services/activityLogService');
          // Get business ID if available
          const { data: businessData } = await supabase
            .from('businesses')
            .select('id')
            .eq('owner_id', data.user.id)
            .single();
          await logLogin(data.user.id, businessData?.id);
        } catch (logError) {
          // Don't fail login if activity logging fails
          console.warn('Failed to log login activity:', logError);
        }
      }, 0);

      // Update loading stage and trigger login animation ONLY after successful authentication
      setLoadingStage('Waking up Nexus...');
      setShowLoginAnimation(true);
      // Small delay to let animation start
      await new Promise(resolve => setTimeout(resolve, 300));

      // IMPORTANT: Check subscription status BEFORE setup status
      // This ensures users can't bypass subscription selection
      const { data: businessCheck } = await supabase
        .from('businesses')
        .select(
          'subscription_status, subscription_past_due_at, active_plan_id, frozen, frozen_reason, stripe_subscription_id',
        )
        .eq('owner_id', data.user.id)
        .single();

      // Check if account is frozen (highest priority check)
      if (businessCheck && businessCheck.frozen === true) {
        const frozenReason = businessCheck.frozen_reason || '';
        const hasActivePlanId = businessCheck.active_plan_id && businessCheck.active_plan_id.trim() !== '';
        const hasStripeSub =
          typeof businessCheck.stripe_subscription_id === 'string' &&
          businessCheck.stripe_subscription_id.trim() !== '';
        const isSubscriptionRelated = frozenReason.includes('Subscription') || 
                                     frozenReason.includes('payment failed') ||
                                     frozenReason.includes('payment stopped');
        
        if (isSubscriptionRelated && hasStripeSub) {
          // Existing Stripe subscription — pay / update card on that subscription, do not buy a second one
          console.log('🧊 Frozen / payment issue with existing Stripe subscription — billing recovery');
          localStorage.removeItem('enhancedLoginInProgress');
          navigate('/admin-subscription-management', {
            state: {
              userId: data.user.id,
              email: email.trim(),
              fromPaymentRecovery: true,
              message:
                frozenReason ||
                'Your subscription payment failed. Update your payment method or pay the open invoice to restore access.',
            },
          });
          return;
        }

        if (isSubscriptionRelated && !hasActivePlanId) {
          // No platform subscription yet — pick a plan
          console.log('🧊 Account frozen due to subscription payment (no active plan), redirecting to subscription management...');
          localStorage.removeItem('enhancedLoginInProgress');
          // Don't sign out - let them access subscription management
          navigate('/admin-subscription-management', { 
            state: { 
              userId: data.user.id,
              email: email.trim(),
              fromFrozenAccount: true,
              message: frozenReason || 'Your subscription payment failed. Please select and activate a subscription plan to continue.' 
            }
          });
          return;
        } else {
          // Account frozen (either by admin OR has active_plan_id but payment failed) - show disabled screen
          console.log('🧊 Account is frozen, showing disabled screen...', { hasActivePlanId, isSubscriptionRelated });
          localStorage.removeItem('enhancedLoginInProgress');
          // Sign out the user
          await supabase.auth.signOut();
          // Redirect to account disabled screen
          navigate('/account-disabled', {
            state: {
              reason: frozenReason || 'Your account has been disabled. Please contact us to find out why.',
              email: email.trim()
            }
          });
          return;
        }
      }

      if (
        businessCheck &&
        !stripeSyncedStatusAllowsNexusMainApp(
          businessCheck.subscription_status,
          (businessCheck as { subscription_past_due_at?: string | null }).subscription_past_due_at,
        )
      ) {
        const pastDueLock =
          String(businessCheck.subscription_status || '')
            .trim()
            .toLowerCase() === 'past_due' &&
          pastDueBillingLockRequired(
            (businessCheck as { subscription_past_due_at?: string | null }).subscription_past_due_at,
          );
        console.log(
          '❌ Subscription not in an allowed Stripe-synced state, redirecting to subscription management...',
          { pastDueLock },
        );
        navigate('/admin-subscription-management', {
          state: {
            userId: data.user.id,
            email: email.trim(),
            fromPastDueLock: pastDueLock,
            fromSignup: !pastDueLock,
            message: pastDueLock
              ? 'Your subscription is more than 4 days past due. Pay the open invoice or update your payment method to restore access.'
              : 'Please select and activate your subscription to continue.',
          },
        });
        return;
      }

      // Check if user has completed setup
      const { data: setupData } = await supabase
        .from('user_setup_status')
        .select('*')
        .eq('user_id', data.user.id)
        .single();

      if (setupData && !setupData.completed) {
        // User has subscription but hasn't completed setup
        console.log('⚠️ User setup not completed, redirecting to setup...');
        navigate('/setup', { 
          state: { 
            userId: data.user.id,
            email: email.trim(),
            message: 'Please complete your setup to continue.' 
          }
        });
        return;
      }

      console.log('✅ Subscription active and setup completed, loading business data...');

      // Now do all the initialization work that would normally happen in AuthRedirectHandler
      const targetRoute = await loadBusinessesAndDetermineRoute(data.user.id);

      // If targetRoute is null, the user was redirected to subscription management
      if (targetRoute === null) {
        console.log('✅ User redirected to subscription management');
        return; // Exit early, navigation already handled
      }

      setLoadingStage('Analyzing Data.........');

      // SIMPLE: Don't store business data in localStorage at all
      // BusinessContext will load it fresh from database when it detects the session
      // This eliminates all race conditions and stale data issues
      
      // Don't cache home screen route - let HomeScreenRedirect always query fresh
      // This ensures resources see the home screen immediately when an admin changes it
      // Clear any stale cache
      localStorage.removeItem('homeScreenRoute');
      
      console.log('✅ Login complete - BusinessContext will load business data fresh from database');

      // Small delay for UI to settle (reduced for better performance)
      await new Promise(resolve => setTimeout(resolve, 200));

      // Handle Remember Me functionality
      // Removed remember me logic as per edit hint

      // Navigate to root route so HomeScreenRedirect can query fresh home screen setting
      // This ensures resources always see the latest home screen when an admin changes it
      console.log('✅ Full initialization complete, navigating to root for home screen redirect');

      // CRITICAL: Restore push notification subscription after successful login
      // This ensures subscriptions are restored even if they were lost during logout/login cycle
      // Run in background (non-blocking) so it doesn't delay navigation
      setTimeout(async () => {
        try {
          const { pushNotificationService } = await import('../../lib/pushNotifications');
          console.log('🔄 Restoring push notification subscription after login...');
          await pushNotificationService.restoreSubscriptionIfNeeded?.();
          // Also trigger validation to ensure subscription is healthy
          await pushNotificationService.validateAndRestoreSubscription?.();
        } catch (pushError) {
          console.warn('Failed to restore push subscription after login (non-critical):', pushError);
        }
      }, 1000); // Wait 1 second after navigation to ensure app is loaded

      // Navigate to root route (/) so HomeScreenRedirect can query fresh home screen setting
      // This ensures all users (admins and resources) always see the latest home screen
      // when an admin changes it, without needing to log out and back in
      navigate('/', { replace: true });
      
    } catch (err) {
      console.error('Login error:', err);
      
      // Clear the flag on error
      localStorage.removeItem('enhancedLoginInProgress');
      
      // Hide login animation on error
      setShowLoginAnimation(false);
      
      let errorMessage = 'An error occurred during login';
      
      if (err instanceof Error) {
        if (err.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password';
          // Clear saved credentials if login fails with invalid credentials
          // Removed remember me logic as per edit hint
        } else if (err.message.includes('rate limit')) {
          errorMessage = 'Too many login attempts. Please wait a few minutes and try again.';
        } else if (err.message.includes('email not confirmed')) {
          errorMessage = 'Please verify your email address before logging in.';
        } else if (err.message.includes('Failed to load business data')) {
          errorMessage = 'Login successful, but failed to load your business data. Please try refreshing the page.';
        } else if (err.message.includes('No active subscription')) {
          errorMessage = 'No active subscription. Please contact your administrator to activate your subscription.';
        } else if (err.message.includes('account access has been suspended')) {
          errorMessage = 'Your account access has been suspended. Please contact your administrator.';
        }
      }

      setError(errorMessage);
      setPassword(''); // Clear password on error
    } finally {
      setLoading(false);
      setLoadingStage('');
    }
  };

  // Show basic loading screen only while checking if user is already logged in
  // The fancy LoadingScreen is only used during actual login process (showLoginAnimation)
  // Show loading screen only if we're actually on the login page
  // Prevents showing loading screen if user navigates away
  if (isLoading && window.location.pathname === '/login') {
    return <BasicLoadingScreen />;
  }
  
  // If not on login page, don't render anything (component shouldn't be mounted)
  if (window.location.pathname !== '/login') {
    return null;
  }

  const isMobile = window.innerWidth < 640;

  return (
    <div 
      className="min-h-screen flex relative overflow-hidden bg-white" 
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      <style>{`
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        @keyframes nexusPulse {
          0%, 100% {
            opacity: 0.15;
            transform: scale(1);
          }
          50% {
            opacity: 0.28;
            transform: scale(1.06);
          }
        }
        @keyframes nexusPulseBlob {
          0%, 100% {
            opacity: 0.25;
          }
          50% {
            opacity: 0.4;
          }
        }
        .nexus-pulse-blob {
          animation: nexusPulseBlob 5s ease-in-out infinite;
        }
        .nexus-pulse-blob-2 {
          animation: nexusPulseBlob 5.5s ease-in-out infinite;
          animation-delay: 0.8s;
        }
        .nexus-pulse-blob-3 {
          animation: nexusPulseBlob 6s ease-in-out infinite;
          animation-delay: 1.6s;
        }
        .nexus-pulse-core {
          animation: nexusPulse 5s ease-in-out infinite;
        }
      `}</style>
      {/* Desktop Login Animation Overlay */}
      {showLoginAnimation && (
        <div 
          className="hidden lg:block fixed inset-0 z-50"
          style={{ background: '#000' }}
        >
          {/* Background Pattern Overlay */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                radial-gradient(circle at 1px 1px, rgba(148, 163, 184, 0.08) 1px, transparent 0),
                radial-gradient(circle at 1px 1px, rgba(99, 102, 241, 0.06) 1px, transparent 0)
              `,
              backgroundSize: '40px 40px, 60px 60px',
              backgroundPosition: '0 0, 20px 20px'
            }}
          />
          
          {/* Gradient Blobs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div 
              className="absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl opacity-30"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.5), rgba(59, 130, 246, 0.4))'
              }}
            />
            <div 
              className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl opacity-25"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(99, 102, 241, 0.3))'
              }}
            />
            <div 
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-15"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(139, 92, 246, 0.3))'
              }}
            />
          </div>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="solar-system solar-system-centered">
              {/* Orbital rings */}
              <div className="orbit orbit-1"></div>
              <div className="orbit orbit-2"></div>
              <div className="orbit orbit-3"></div>
              <div className="orbit orbit-4"></div>
              
              {/* Positioned orbs */}
              <div className="orb orb-blue"></div>
              <div className="orb orb-purple"></div>
              <div className="orb orb-pink"></div>
              <div className="orb orb-blue-2"></div>
              <div className="orb orb-purple-2"></div>
              <div className="orb orb-pink-2"></div>
              <div className="orb orb-blue-3"></div>
              
              {/* Small particles */}
              <div className="particle particle-1"></div>
              <div className="particle particle-2"></div>
              <div className="particle particle-3"></div>
              <div className="particle particle-4"></div>
            </div>
          </div>
          
          {/* Morphing Loading Text */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="text-center">
              <div className="login-loading-text">
                {(loadingStage || 'Waking up Nexus.....').replace(/\.+$/, '')}
                <span className="animated-dots">
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Login Animation Overlay */}
      {showLoginAnimation && (
        <div 
          className="lg:hidden fixed inset-0 z-50"
          style={{ background: '#000' }}
        >
          {/* Background Pattern Overlay */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                radial-gradient(circle at 1px 1px, rgba(148, 163, 184, 0.08) 1px, transparent 0),
                radial-gradient(circle at 1px 1px, rgba(99, 102, 241, 0.06) 1px, transparent 0)
              `,
              backgroundSize: '40px 40px, 60px 60px',
              backgroundPosition: '0 0, 20px 20px'
            }}
          />
          
          {/* Gradient Blobs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div 
              className="absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl opacity-30"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.5), rgba(59, 130, 246, 0.4))'
              }}
            />
            <div 
              className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl opacity-25"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(99, 102, 241, 0.3))'
              }}
            />
            <div 
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-15"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(139, 92, 246, 0.3))'
              }}
            />
          </div>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="mobile-solar-system mobile-solar-system-centered">
              {/* Mobile orbital rings */}
              <div className="mobile-orbit mobile-orbit-1"></div>
              <div className="mobile-orbit mobile-orbit-2"></div>
              <div className="mobile-orbit mobile-orbit-3"></div>
              
              {/* Mobile orbs */}
              <div className="mobile-orb mobile-orb-1 mobile-orb-enhanced"></div>
              <div className="mobile-orb mobile-orb-2 mobile-orb-enhanced"></div>
              <div className="mobile-orb mobile-orb-3 mobile-orb-enhanced"></div>
              <div className="mobile-orb mobile-orb-4 mobile-orb-enhanced"></div>
              <div className="mobile-orb mobile-orb-5 mobile-orb-enhanced"></div>
            </div>
          </div>
          
          {/* Mobile Loading Text */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="text-center">
              <div className="mobile-loading-text">
                {(loadingStage || 'Waking up Nexus.....').replace(/\.+$/, '')}
                <span className="animated-dots">
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Form Container */}
      <div className={`flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 min-h-screen relative z-10 transition-transform duration-700 ease-in-out ${
        showLoginAnimation ? 'lg:-translate-x-full' : ''
      }`}>
        <div className="w-full max-w-md">
          {/* Card Container - Dark Glassmorphism */}
          <div className="bg-white rounded-t-[20px] sm:rounded-[24px] shadow-[0_2px_16px_rgba(0,0,0,0.06)] overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="bg-gray-50/60 border-b border-gray-100 px-6 py-6 text-center">
              <h1 className="text-2xl font-bold text-gray-900" style={{ 
                letterSpacing: '-0.02em'
              }}>
                Welcome
              </h1>
              <p className="text-gray-500 text-sm mt-1">Log in to continue to your Nexus Suite</p>
            </div>

            {/* Form Content */}
            <form 
              onSubmit={handleLogin} 
              className="flex-1 overflow-y-auto"
              style={{ 
                WebkitOverflowScrolling: 'touch',
                paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
              }}
            >
              <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-5`}>
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                    {error}
                  </div>
                )}

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base text-gray-900 placeholder-gray-400 bg-white border ${
                      validationErrors.email ? 'border-red-300' : 'border-gray-200'
                    } rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation`}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{ 
                      fontSize: '16px', 
                      minHeight: isMobile ? '44px' : '48px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  />
                  {validationErrors.email && (
                    <p className="mt-1.5 text-sm text-red-600">{validationErrors.email}</p>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="password" className="block text-xs font-bold text-gray-700">
                      Password
                    </label>
                    <Link
                      to="/reset-password"
                      className="text-xs font-semibold text-gray-500 hover:text-gray-900 underline underline-offset-2 transition-colors"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Forgot?
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      className={`w-full ${isMobile ? 'px-3 py-2.5 pr-12' : 'px-4 py-3 pr-12'} text-base text-gray-900 placeholder-gray-400 bg-white border ${
                        validationErrors.password ? 'border-red-300' : 'border-gray-200'
                      } rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation`}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{ 
                        fontSize: '16px', 
                        minHeight: isMobile ? '44px' : '48px',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-100 active:bg-gray-200 active:scale-95"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <p className="mt-1.5 text-sm text-red-600">{validationErrors.password}</p>
                  )}
                </div>

                {/* Login Button - iOS Style */}
                <div className={`${isMobile ? 'pt-2' : 'pt-3'}`}>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-6 py-3 text-base'} font-semibold text-white rounded-full transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${
                      loading
                        ? 'bg-gray-500 cursor-not-allowed'
                        : 'bg-gray-900 hover:bg-black active:bg-black'
                    } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                        {loadingStage || 'Connecting...'}
                      </div>
                    ) : (
                      'Log in'
                    )}
                  </button>
                </div>

                <div className="text-center pt-2">
                  <p className="text-sm text-gray-500">
                    Don't have an account?{' '}
                    <Link
                      to="/signup"
                      className="font-semibold text-gray-900 underline underline-offset-2 hover:text-gray-600 transition-colors"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Sign up
                    </Link>
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right Side - Marketing Content (Desktop Only), landing-page style */}
      <div className={`hidden lg:flex flex-1 items-center justify-center relative overflow-hidden transition-all duration-700 ease-in-out ${
        showLoginAnimation ? 'lg:translate-x-1/2 lg:scale-125' : ''
      }`}>
        <div className="relative z-20 px-8 xl:px-12 max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 border border-gray-200 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-600"><Sparkles className="h-3.5 w-3.5 text-emerald-500" /> The Ultimate Service Company Builder</span>
          <h1 className="mt-6 text-4xl xl:text-[52px] font-extrabold leading-[1.05] tracking-tight text-gray-900">
            Welcome Back to <span className="text-blue-600">Nexus</span>
          </h1>
          <p className="mt-5 text-lg text-gray-600">
            One platform to run your entire operation. CRM, scheduling, field management,
            marketing, and AI — all connected, all in one place.
          </p>
        </div>
      </div>
    </div>
  );
}